import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertTriangle, AlertCircle, Info, X } from 'lucide-react';
import { cn } from '../lib/cn.js';

export type ToastVariant = 'success' | 'warning' | 'danger' | 'info';

export interface ToastInput {
  title: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number; // ms; 0 = persistent (errors)
}

interface ToastInternal extends ToastInput {
  id: number;
  variant: ToastVariant;
}

interface ToastContextValue {
  toast: (t: ToastInput) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

let idSeq = 1;

const variantIcon = {
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle,
  info: Info,
} as const;

const variantClass: Record<ToastVariant, string> = {
  success: 'border-success/40 bg-success-muted',
  warning: 'border-warning/40 bg-warning-muted',
  danger: 'border-danger/40 bg-danger-muted',
  info: 'border-info/40 bg-info-muted',
};

const iconClass: Record<ToastVariant, string> = {
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  info: 'text-info',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastInternal[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((t: ToastInput) => {
    const id = idSeq++;
    const variant = t.variant ?? 'info';
    setToasts((cur) => [...cur, { ...t, id, variant }]);
    const duration = t.duration ?? (variant === 'danger' ? 0 : 5000);
    if (duration > 0) {
      window.setTimeout(() => {
        setToasts((cur) => cur.filter((x) => x.id !== id));
      }, duration);
    }
    return id;
  }, []);

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            aria-live="polite"
            aria-atomic="false"
            className="pointer-events-none fixed top-4 right-4 z-50 flex w-[min(360px,calc(100vw-2rem))] flex-col gap-2"
          >
            {toasts.map((t) => (
              <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastInternal; onDismiss: () => void }) {
  const Icon = variantIcon[toast.variant];
  // simple fade-in
  const [show, setShow] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setShow(true), 10);
    return () => window.clearTimeout(id);
  }, []);
  return (
    <div
      role="status"
      className={cn(
        'pointer-events-auto flex items-start gap-3 rounded-md border bg-bg-surface-2 p-4 shadow-lg transition-all duration-200',
        variantClass[toast.variant],
        show ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1',
      )}
    >
      <Icon className={cn('size-5 shrink-0 mt-0.5', iconClass[toast.variant])} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-base font-semibold text-text-primary leading-tight">{toast.title}</p>
        {toast.description && <p className="mt-1 text-sm text-text-secondary">{toast.description}</p>}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="-mr-1 -mt-1 rounded p-1 text-text-secondary hover:text-text-primary hover:bg-bg-surface-3"
      >
        <X className="size-4" aria-hidden />
      </button>
    </div>
  );
}
